import { Injectable } from '@angular/core';
import { FeatureFlag } from '@app/enums';
import { DataField } from '@app/models/settings/data-field.model';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { Column } from '@import/classes';
import { customFieldLocations } from '@import/enums/custom-field-locations.enum';
import { InputType } from '@import/enums/input-type.enum';
import { addDataFieldsToMappingTables } from '@import/functions/add-data-fields-mapping-tables.function';
import { getMappingTableColumnTypes } from '@import/functions/get-mapping-table-column-types';
import { dataFieldToColumnType } from '@import/functions/model-transformations/data-field-to-column-type.function';
import { ColumnType } from '@import/interfaces';
import { UnmatchedItems } from '@import/interfaces/unmatched-items.interface';
import { EmployeeImporterMappingTables } from '@import/meta/employee-importer-mapping-tables.meta';
import { PersonalInformationColumnsTypes } from '@import/meta/personal-information-columns-types.meta';
import { MappingTables } from '@import/types/mapping-tables.types';
import { flatten } from 'lodash-es';

/**
 * This service is contains the EmployeeImporterMappingTables, used for the EmployeeImporter
 * The defined functions are generally meant to initialize 'dynamic' columns and column option
 * that require some sort of call to the backend.
 */
@Injectable()
export class EmployeeHumiColumnService {
    mappedColumns: ColumnType[] = null;
    employeeMappingTables = EmployeeImporterMappingTables;

    constructor(
        protected auth: AuthService,
        protected notify: NotifyService,
        private featureService: FeatureService
    ) {
        this.featureService.has(FeatureFlag.birthdayVisibility).then((shouldShowBirthdayVisibility) => {
            if (!shouldShowBirthdayVisibility) {
                this.employeeMappingTables.personal.sections.default = PersonalInformationColumnsTypes.filter(
                    function (column) {
                        return column.serializeName !== 'isBirthdayPrivate';
                    }
                );
            }
        });
    }

    getMappingTables(): MappingTables {
        return this.employeeMappingTables;
    }

    updateMappedColumns(): void {
        this.mappedColumns = this.getMappedHumiColumns();
    }

    setDataFieldsColumns(): Promise<ColumnType[]> {
        const dataFieldColumns: ColumnType[] = [];

        return DataField.param('company', this.auth.company.id)
            .with(['dataFieldGroup', 'dataFieldOptions'])
            .all()
            .then(([dataFields]) => {
                const personal = dataFields.filter((dataField: DataField) =>
                    dataField.dataFieldGroup.isLocation('personal')
                );
                const jobAndPay = dataFields.filter((dataField: DataField) =>
                    dataField.dataFieldGroup.isLocation('job_and_pay')
                );

                addDataFieldsToMappingTables(personal, this.employeeMappingTables, customFieldLocations.personal);
                addDataFieldsToMappingTables(jobAndPay, this.employeeMappingTables, customFieldLocations.job_and_pay);

                [...personal, ...jobAndPay].map(dataFieldToColumnType).forEach((columnType: ColumnType) => {
                    dataFieldColumns.push(columnType);
                });

                return dataFieldColumns;
            });
    }

    setDynamicOptionsForColumnTypes(humiColumns: ColumnType[]): Promise<ColumnType[]> {
        return new Promise((resolve) => {
            const getOptionsPromises = humiColumns.map((columnType: ColumnType) => {
                if (columnType.input === InputType.options && columnType.getOptions) {
                    return columnType.getOptions(this.auth);
                }
            });

            Promise.all(getOptionsPromises)
                .then(() => {
                    resolve(humiColumns);
                })
                .catch((err) => this.notify.error(err || 'Unable to retrieve options'));
        });
    }

    /**
     * Returns a list of unmatched csvColumns as UnmatchedItems
     * to be displayed in the UnmatchedItems component
     */
    getUnmatchedColumnItems(csvColumns: Column[]): UnmatchedItems[] {
        const humiColumns = getMappingTableColumnTypes(this.employeeMappingTables)
            .map((humiColumn) => humiColumn.csvColumn)
            .filter(Boolean);
        return csvColumns
            .filter((column) => !humiColumns.includes(column))
            .map((csvColumn: Column) => {
                return {
                    index: csvColumn.index,
                    label: csvColumn.name,
                    isEmpty: csvColumn.values.size === 0,
                };
            });
    }

    /**
     * Returns a list of unmatched csvColumn values as UnmatchedItems
     * to be displayed in the UnmatchedItems component
     */
    getUnmatchedColumnValueItems(): UnmatchedItems[] {
        return flatten(
            this.getValueMappableHumiColumns().map((humiColumn: ColumnType) =>
                [...humiColumn.csvColumn.values].map((value: string) => {
                    if (humiColumn.csvColumn.hasValueMapped(value)) {
                        return null;
                    }
                    return {
                        label: value,
                        isEmpty: false,
                    };
                })
            )
        ).filter(Boolean);
    }

    /**
     * Fetch HumiColumns from the mapping table where there is a csvColumn
     * mapped. In addition, sort the HumiColumns by csvColumn indices
     */
    getMappedHumiColumns(): ColumnType[] {
        return getMappingTableColumnTypes(this.employeeMappingTables)
            .filter((humiColumn: ColumnType) => !!humiColumn.csvColumn)
            .sort((humiColumnA: ColumnType, humiColumnB: ColumnType) => {
                // TODO: sUpdate to use primary column from RowColumnService
                if (humiColumnA.serializeName === 'account.email') {
                    return -1;
                }
                return humiColumnA.csvColumn.index - humiColumnB.csvColumn.index;
            });
    }

    /**
     * Fetch CsvColumns from HumiColumns in the mapping table where there is a csvColumn
     * mapped. In addition, sort the HumiColumns by csvColumn indices
     */
    getMappedCsvColumns(): Column[] {
        return this.getMappedHumiColumns().map((humiColumn: ColumnType) => humiColumn.csvColumn);
    }

    /**
     * Fetch HumiColumns from the mapping table where there is a csvColumn
     * mapped, the HumiColumn has known options and csvColumns have data
     */
    getValueMappableHumiColumns(): ColumnType[] {
        return getMappingTableColumnTypes(this.employeeMappingTables).filter(
            (humiColumn: ColumnType) =>
                humiColumn.csvColumn &&
                (humiColumn.input === InputType.options || humiColumn.input === InputType.boolean) &&
                humiColumn.csvColumn.values.size > 0
        );
    }

    getRequiredColumnCount(): number {
        return getMappingTableColumnTypes(this.employeeMappingTables).filter((columnType) => columnType.required)
            .length;
    }

    /**
     * Get a column type from mapping tables by serialize name
     */
    getColumnType(serializeName: string): ColumnType | null {
        const [columnType] = getMappingTableColumnTypes(this.employeeMappingTables).filter(
            (type) => type.serializeName === serializeName
        );

        if (columnType) {
            return columnType;
        }

        return null;
    }

    getIndexBySerializeName(needle: string): null | number {
        const column = getMappingTableColumnTypes(this.employeeMappingTables).find(
            (humiColumn: ColumnType) => humiColumn.serializeName === needle
        );
        return column?.csvColumn?.index;
    }
}
